import { Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material'
import { CircleInformationMajor, FraudProtectMajor, RiskMinor } from '@shopify/polaris-icons'
import { GSIcon } from '../shared/GSIcon'
import { Formik, FormikProps } from 'formik'
import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FormikMultipleFileInput } from 'src/components/shared/form/formik/FormikMultipleFileInput'
import { InsuranceCoverageFormValues } from './InsuranceCoverageForm.interface'
import { createInsuranceCoverageSchema } from './InsuranceCoverageForm.validator'
import { INSURANCE_COVERAGE_MAX_FILES } from './insuranceCoverage.constants'
import { MAX_FILE_SIZE_BYTES } from 'src/constants/fileUpload'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { PendingReturnStepSource, savePendingReturnStep } from 'src/utils/pendingReturnStep'
import { hasTextContent } from 'src/utils/htmlUtils'
import {
  FetchDocusignSigningEnvelopeOptions,
  DocusignCompleteSigning,
  DocusignDeclineSigning,
  DocusignStartSigning,
  FetchDriver,
  FetchOptOutProducts,
  SkipStep,
  UploadInsuranceCoverage,
} from 'src/constants/actionTypes'
import { CLIENT_ROUTES } from 'src/constants/routes'
import {
  DocusignSigningEnvelopeOptionsResponse,
  DocusignSigningField,
  DocusignStartSigningResponse,
} from 'src/interfaces/docusign'
import { OptOutProduct } from 'src/interfaces/optOut'
import { useQuery } from 'src/utils/hooks/useQuery'
import { captureSentryError } from 'src/utils/sentry'
import { showToast } from 'src/utils/toast'
import tw, { css } from 'twin.macro'
import { DocusignFieldsFormModal } from '../docusign-field-form-modal/DocusignFieldsFormModal'
import { Button } from '../shared/Button'
import { FormErrorMessage } from '../shared/form/FormErrorMessage'
import { OfflineBanner } from '../shared/OfflineBanner'
import { useOnlineStatus } from 'src/utils/hooks/useOnlineStatus'
import { useStepFormStorage } from 'src/hooks/useStepFormStorage'
import { SecureWrapper } from '../layout/SecureWrapper'
import { StepLabels } from '../client/step-labels/StepLabels'
import { GigSafeCoverageModal } from './GigSafeCoverageModal'

interface Props {
  stepId?: string
  stepOptional?: boolean
  stepInsurancePolicyProductId?: string
  stepRequireDocumentUploadWhenOptedIn?: boolean
  documentUploadTitleWhenOptedIn?: string
}

const DOCUSIGN_CONTAINER_ID = 'docusign-container'

interface SigningField {
  tabLabel: string
  value: string
}

const formatCost = (product: OptOutProduct): string => {
  const parts: string[] = []

  const feeCents = Number(product.feeCents)
  const feePercent = Number(product.feePercent)
  const feeCapCents = Number(product.feeCapCents)

  if (feeCents > 0) {
    parts.push(`$${(feeCents / 100).toFixed(2)}`)
  }

  if (feePercent > 0) {
    parts.push(`${feePercent}% of earnings`)
  }

  if (parts.length === 0) {
    return ''
  }

  let cost = parts.join(' + ')

  if (feeCapCents > 0) {
    cost += ` (max $${(feeCapCents / 100).toFixed(2)}/week)`
  }

  return cost
}

const InsuranceCoverageFormComponent = ({
  stepId,
  stepOptional,
  stepInsurancePolicyProductId,
  stepRequireDocumentUploadWhenOptedIn,
  documentUploadTitleWhenOptedIn,
}: Props) => {
  const dispatch = useDispatch()
  const { getQuery } = useQuery()
  const navigate = useNavigate()

  // START DOCUSIGN STUFF
  const [docusignSigningData, setDocusignSigningData] =
    useState<DocusignStartSigningResponse | null>(null)
  const [, setSigningEnvelopeOptions] = useState<DocusignSigningEnvelopeOptionsResponse | null>(
    null,
  )
  const [isStartLoading, setIsStartLoading] = useState<boolean>(false)
  const [isSigningFallbackLoading, setIsSigningFallbackLoading] = useState<boolean>(false)
  const [isFieldsModalOpen, setIsFieldsModalOpen] = useState<boolean>(false)
  const [modalFields, setModalFields] = useState<Array<DocusignSigningField>>([])
  const [modalHiddenFields, setModalHiddenFields] = useState<Array<DocusignSigningField>>([])
  const [signingIframeCrashed, setSigningIframeCrashed] = useState<boolean>(false)

  const signingIframeCorsCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const signingIframeExistsCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const signingReadyTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const signingIframeWaitObserverRef = useRef<{
    observer: MutationObserver
    timeoutId: NodeJS.Timeout
  } | null>(null)

  const clearSigningTimers = useCallback(() => {
    if (signingIframeWaitObserverRef.current) {
      clearTimeout(signingIframeWaitObserverRef.current.timeoutId)
      signingIframeWaitObserverRef.current.observer.disconnect()
      signingIframeWaitObserverRef.current = null
    }

    if (signingIframeCorsCheckIntervalRef.current) {
      clearInterval(signingIframeCorsCheckIntervalRef.current)
      signingIframeCorsCheckIntervalRef.current = null
    }

    if (signingIframeExistsCheckIntervalRef.current) {
      clearInterval(signingIframeExistsCheckIntervalRef.current)
      signingIframeExistsCheckIntervalRef.current = null
    }

    if (signingReadyTimeoutRef.current) {
      clearTimeout(signingReadyTimeoutRef.current)
      signingReadyTimeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    return clearSigningTimers
  }, [clearSigningTimers])

  const docusignReturnEvent = getQuery('event')

  useEffect(() => {
    if (!stepId || !docusignReturnEvent) {
      return
    }

    if (docusignReturnEvent === 'signing_complete') {
      navigate(CLIENT_ROUTES.DOCS_URL)

      handleCompleteSigning()
    }
  }, [stepId, docusignReturnEvent])

  const handleLoadDocusignSigningEnvelopeOptions = useCallback(async () => {
    try {
      const response: DocusignSigningEnvelopeOptionsResponse | undefined = await createAsyncAction(
        dispatch,
        FetchDocusignSigningEnvelopeOptions.request({
          stepId,
        }),
      )

      if (!response) {
        throw new Error('Signing envelope options are not available')
      }

      setSigningEnvelopeOptions(response)
    } catch (err) {
      // ignore this error
    }
  }, [stepId])

  useEffect(() => {
    handleLoadDocusignSigningEnvelopeOptions()
  }, [stepId])

  const handleStartSigning = useCallback(
    async (signingFields?: Array<SigningField>) => {
      setIsStartLoading(true)
      setSigningIframeCrashed(false)

      try {
        const signingResponse: DocusignStartSigningResponse | undefined = await createAsyncAction(
          dispatch,
          DocusignStartSigning.request({
            signingFields,
            hiddenFields: modalHiddenFields,
            stepId,
            embedded: true,
          }),
        )

        if (!signingResponse) {
          throw new Error('Signing is not available')
        }

        if (signingResponse.retryFetchDriver) {
          await createAsyncAction(dispatch, FetchDriver.request())

          return
        }

        if (signingResponse.signingFields?.length) {
          setModalFields(signingResponse.signingFields)
          setModalHiddenFields(signingResponse.hiddenFields || [])
          setIsFieldsModalOpen(true)
          setIsStartLoading(false)

          return
        }

        setDocusignSigningData(signingResponse)
        handleLoadDocusign(signingResponse)
      } catch (err) {
        captureSentryError(err, { message: '[InsuranceCoverageForm.handleStartSigning]' })

        showToast('Failed to load signing page, please try again later', {
          variant: 'error',
        })
      } finally {
        setIsStartLoading(false)
      }
    },
    [stepId, modalHiddenFields],
  )

  const handleStartFallbackSigning = useCallback(async () => {
    setIsSigningFallbackLoading(true)

    const newTab = window.open('', '_blank')

    try {
      const signingResponse: DocusignStartSigningResponse | undefined = await createAsyncAction(
        dispatch,
        DocusignStartSigning.request({
          stepId,
          embedded: false,
        }),
      )

      if (!signingResponse) {
        throw new Error('Signing is not available')
      }

      if (signingResponse.retryFetchDriver) {
        newTab?.close()
        await createAsyncAction(dispatch, FetchDriver.request())

        return
      }

      if (signingResponse.signingData?.url && newTab) {
        if (stepId) {
          savePendingReturnStep(PendingReturnStepSource.Docusign, stepId)
        }

        newTab.location.href = signingResponse.signingData.url
      } else {
        newTab?.close()
        throw new Error('Signing is not available')
      }
    } catch (err) {
      newTab?.close()
      captureSentryError(err, { message: '[InsuranceCoverageForm.handleStartFallbackSigning]' })

      showToast('Failed to load signing page, please try again later', {
        variant: 'error',
      })
    } finally {
      setIsSigningFallbackLoading(false)
    }
  }, [stepId])

  const handleCompleteSigning = useCallback(async () => {
    try {
      await createAsyncAction(dispatch, DocusignCompleteSigning.request({ stepId }))
    } catch (err) {
      showToast('Failed to finish signing, please try again later', {
        variant: 'error',
      })
    }

    await createAsyncAction(dispatch, FetchDriver.request())
  }, [stepId])

  const handleDeclineSigning = useCallback(async () => {
    try {
      await createAsyncAction(dispatch, DocusignDeclineSigning.request({ stepId }))
      await createAsyncAction(dispatch, FetchDriver.request())
    } catch (err) {
      // ignore this error
    }
  }, [stepId])

  const handleLoadDocusign = useCallback(
    async (payload: DocusignStartSigningResponse) => {
      if (!payload.signingData?.url) {
        return
      }

      try {
        if (!window.DocuSign) {
          throw new Error('DocuSign SDK is not loaded')
        }

        const docusign = await window.DocuSign.loadDocuSign(
          process.env.REACT_APP_DOCUSIGN_INTEGRATION_KEY as string,
        )

        const signing = docusign.signing({
          url: payload.signingData.url,
          displayFormat: 'focused',
          style: {
            branding: {
              primaryButton: {
                backgroundColor: '#333',
                color: '#fff',
              },
            },
            signingNavigationButton: {
              finishText: 'Continue',
              position: 'bottom-center',
            },
          },
        })

        let isDocusignReady = false

        signing.on('ready', () => {
          isDocusignReady = true
          setIsStartLoading(false)

          if (signingReadyTimeoutRef.current) {
            clearTimeout(signingReadyTimeoutRef.current)
          }
        })

        signing.on('sessionEnd', async (event) => {
          clearSigningTimers()

          if (event.sessionEndType === 'signing_complete') {
            handleCompleteSigning()

            return
          }

          if (event.sessionEndType === 'ttl_expired') {
            setDocusignSigningData(null)
            showToast('Signing link is expired, please start signing again', {
              variant: 'error',
            })

            return
          }

          if (event.sessionEndType === 'decline') {
            await handleDeclineSigning()
            setDocusignSigningData(null)
            showToast('Signing is declined, please start signing again or contact support', {
              variant: 'error',
            })

            return
          }
        })

        signing.mount(`#${DOCUSIGN_CONTAINER_ID}`)

        signingReadyTimeoutRef.current = setTimeout(() => {
          if (!isDocusignReady) {
            setSigningIframeCrashed(true)

            captureSentryError(new Error('Docusign ready event not received within timeout'), {
              message: '[handleLoadDocusign]',
            })
          }
        }, 30000)

        startSigningIframeListeners()
      } catch (err: any) {
        setIsStartLoading(false)
        setDocusignSigningData(null)
        setSigningIframeCrashed(true)
        captureSentryError(err, {
          message: '[handleLoadDocusign]',
        })
      }
    },
    [clearSigningTimers],
  )

  // Any iframe in our container: the SDK empties the container before mounting the signing iframe,
  // so it is the only one there, whatever id DocuSign gives it (plain `js-library-iframe` in older
  // SDK versions, `js-library-iframe-<instanceId>` now). Not a document-wide id lookup, because the
  // SDK also renders hidden `js-library-iframe-preload-*` iframes into <body>.
  const findDocusignIframe = useCallback((): HTMLElement | null => {
    return document.querySelector<HTMLElement>(`#${DOCUSIGN_CONTAINER_ID} iframe`)
  }, [])

  // Load progress is tracked by the SDK `ready` event (see handleLoadDocusign), not the iframe
  // `load` event: `load` waits for every DocuSign sub-resource and routinely takes over 10s on
  // slow connections while the signing UI is already usable.
  const startSigningIframeListeners = useCallback(() => {
    const IFRAME_WAIT_TIMEOUT_MS = 5000
    const IFRAME_EXISTS_CHECK_INTERVAL_MS = 10000

    const attachListeners = (iframe: HTMLElement) => {
      iframe.addEventListener('error', () => {
        setSigningIframeCrashed(true)

        captureSentryError(new Error('Docusign iframe is crashed'), {
          message: '[startSigningIframeListeners]',
        })
      })

      signingIframeExistsCheckIntervalRef.current = setInterval(() => {
        if (!findDocusignIframe()) {
          setSigningIframeCrashed(true)

          captureSentryError(new Error('Docusign iframe is not found'), {
            message: '[startSigningIframeListeners]',
          })
        }
      }, IFRAME_EXISTS_CHECK_INTERVAL_MS)
    }

    const existingIframe = findDocusignIframe()

    if (existingIframe) {
      attachListeners(existingIframe)

      return
    }

    const timeoutId = setTimeout(() => {
      signingIframeWaitObserverRef.current?.observer.disconnect()
      signingIframeWaitObserverRef.current = null
      setSigningIframeCrashed(true)

      captureSentryError(new Error('Docusign iframe is not found'), {
        message: '[startSigningIframeListeners]',
      })
    }, IFRAME_WAIT_TIMEOUT_MS)

    const observer = new MutationObserver(() => {
      const iframe = findDocusignIframe()

      if (iframe) {
        clearTimeout(timeoutId)
        observer.disconnect()
        signingIframeWaitObserverRef.current = null
        attachListeners(iframe)
      }
    })

    signingIframeWaitObserverRef.current = { observer, timeoutId }

    observer.observe(document.body, { childList: true, subtree: true })
  }, [findDocusignIframe])

  useEffect(() => {
    if (!signingIframeCrashed) {
      return
    }

    setDocusignSigningData(null)
    setIsStartLoading(false)
    clearSigningTimers()

    showToast('Failed to start signing, please try again', {
      variant: 'error',
    })
  }, [signingIframeCrashed, clearSigningTimers])

  const handleCloseFieldsModal = useCallback(() => {
    setIsFieldsModalOpen(false)
  }, [])

  const handleSubmitFieldsModal = useCallback(
    async (signingFields?: Array<SigningField>) => {
      await handleStartSigning(signingFields)

      setIsFieldsModalOpen(false)
    },
    [handleStartSigning],
  )
  // END DOCUSIGN STUFF

  const [optOutProduct, setOptOutProduct] = useState<OptOutProduct | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [isCoverageModalOpen, setIsCoverageModalOpen] = useState<boolean>(false)
  const [checkedRequirements, setCheckedRequirements] = useState<Record<number, boolean>>({})
  const isOnline = useOnlineStatus()

  const { savedValues, onValuesChange, clearStorage } = useStepFormStorage<{
    type: 'gigsafe' | 'own' | 'skip' | null
  }>({
    storageKey: `insurance_coverage_form_${stepId}`,
    fileFields: ['files'],
  })

  const handleFormSubmit = async (values: InsuranceCoverageFormValues) => {
    if (!stepId) {
      return
    }

    setIsLoading(true)
    setError(null)

    const requestBody: any = {
      optOut: values.type === 'own',
      stepId,
    }

    if (values.files && values.files.length > 0) {
      requestBody.files = values.files
    }

    try {
      if (values.type === 'skip') {
        await createAsyncAction(dispatch, SkipStep.request({ stepId }))
      } else {
        await createAsyncAction(dispatch, UploadInsuranceCoverage.request(requestBody))
      }

      clearStorage()
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const formikRef = useRef<FormikProps<InsuranceCoverageFormValues>>(null)

  const handleClick = async (values: InsuranceCoverageFormValues) => {
    if (values.type === 'skip') {
      await handleFormSubmit(values)

      return
    }

    if (values.type === 'gigsafe' && optOutProduct?.docusignTemplateId) {
      if (stepRequireDocumentUploadWhenOptedIn) {
        await handleFormSubmit(values)
      }

      handleStartSigning()

      return
    }

    await handleFormSubmit(values)
  }

  useEffect(() => {
    const fetchProducts = async () => {
      const product: OptOutProduct = await createAsyncAction(
        dispatch,
        FetchOptOutProducts.request({
          stepId,
          productId: stepInsurancePolicyProductId,
        }),
      )

      setOptOutProduct(product)
    }

    fetchProducts()
  }, [stepId, stepInsurancePolicyProductId])

  const initialValues: InsuranceCoverageFormValues = useMemo(() => {
    if (!optOutProduct?.allowOptOut && !stepOptional) {
      return { type: 'gigsafe', files: [] }
    }

    return {
      type: savedValues?.type ?? null,
      files: [],
    }
  }, [optOutProduct?.allowOptOut, savedValues, stepOptional])

  const insuranceRequirements = useMemo(() => {
    const requirements = optOutProduct?.insuranceRequirements

    return hasTextContent(requirements) ? requirements : null
  }, [optOutProduct?.insuranceRequirements])

  const costDisplay = useMemo(() => {
    if (!optOutProduct) {
      return null
    }

    return formatCost(optOutProduct)
  }, [optOutProduct])

  const coverageDisplay = useMemo(() => optOutProduct?.coverage || null, [optOutProduct?.coverage])

  const hasCoverageModalContent = useMemo(
    () =>
      !!costDisplay ||
      !!coverageDisplay ||
      hasTextContent(optOutProduct?.coverageDetails) ||
      !!optOutProduct?.coverageDetailsPdfUrl,
    [
      costDisplay,
      coverageDisplay,
      optOutProduct?.coverageDetails,
      optOutProduct?.coverageDetailsPdfUrl,
    ],
  )

  const ownInsuranceRequirements = useMemo(
    () => optOutProduct?.manualUploadRequirements || [],
    [optOutProduct?.manualUploadRequirements],
  )

  const allRequirementsChecked = useMemo(() => {
    if (ownInsuranceRequirements.length === 0) {
      return true
    }

    return ownInsuranceRequirements.every((_requirement, index) => checkedRequirements[index])
  }, [ownInsuranceRequirements, checkedRequirements])

  const validationSchema = useMemo(
    () => createInsuranceCoverageSchema(stepRequireDocumentUploadWhenOptedIn),
    [stepRequireDocumentUploadWhenOptedIn],
  )

  const handleOpenCoverageModal = useCallback(() => {
    setIsCoverageModalOpen(true)
  }, [])

  const handleCloseCoverageModal = useCallback(() => {
    setIsCoverageModalOpen(false)
  }, [])

  const handleToggleRequirement = useCallback((requirementIndex: number) => {
    setCheckedRequirements((prev) => ({
      ...prev,
      [requirementIndex]: !prev[requirementIndex],
    }))
  }, [])

  const handleSelectGigSafe = useCallback((setFieldValue: (field: string, value: any) => void) => {
    setFieldValue('type', 'gigsafe')
    setFieldValue('files', null)
  }, [])

  const handleSelectOwnInsurance = useCallback(
    (setFieldValue: (field: string, value: any) => void) => {
      setFieldValue('type', 'own')
      setFieldValue('files', null)
      setCheckedRequirements({})
    },
    [],
  )

  if (!optOutProduct) {
    return <CircularProgress />
  }

  const hasDocumentUploadTitleContent = hasTextContent(documentUploadTitleWhenOptedIn)

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={handleClick}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, values, setFieldValue, submitCount }) => {
        onValuesChange({ type: values.type })

        const isOwnInsuranceSelected = values.type === 'own'
        const isGigSafeSelected = values.type === 'gigsafe' || !optOutProduct.allowOptOut

        const showTypeRequiredError = submitCount > 0 && !values.type

        const canSubmit =
          !isOwnInsuranceSelected ||
          Boolean(values.files && values.files.length > 0 && allRequirementsChecked)

        return (
          <SecureWrapper
            footer={
              docusignSigningData ? null : (
                <Fragment>
                  <OfflineBanner isOnline={isOnline} />
                  <FormErrorMessage css={tw`mb-4`} error={error} />
                  {signingIframeCrashed && values.type === 'gigsafe' ? (
                    <Button
                      className={tw`w-full`}
                      loading={isSigningFallbackLoading}
                      disabled={!isOnline}
                      color="light"
                      onClick={handleStartFallbackSigning}
                      data-testid="contractors-onboarding-insurance-coverage-sign-new-tab-btn"
                    >
                      Sign the contract in a new tab
                    </Button>
                  ) : (
                    <Button
                      className={tw`w-full mt-4`}
                      loading={isStartLoading || isLoading}
                      disabled={!isOnline || !canSubmit}
                      onClick={() => {
                        handleSubmit()
                      }}
                      data-testid="contractors-onboarding-insurance-coverage-submit-btn"
                    >
                      Select and Continue
                    </Button>
                  )}
                </Fragment>
              )
            }
          >
            <div>
              <div
                css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
              >
                <FraudProtectMajor width={20} />
              </div>
              <StepLabels />
              <Typography css={tw`my-2`} variant="h2">
                {optOutProduct?.insuranceProduct.name || 'Insurance Coverage'}
              </Typography>

              {(insuranceRequirements || hasCoverageModalContent) && (
                <div css={tw`mb-4 bg-shades-0 rounded-xl overflow-hidden`}>
                  {insuranceRequirements && (
                    <Fragment>
                      <div css={tw`flex flex-row items-center px-4 py-5`}>
                        <GSIcon
                          iconName="gs_icon_clipboard"
                          width={16}
                          height={16}
                          css={tw`mr-2`}
                        />
                        <Typography css={tw`text-base font-semibold text-shades-900`} variant="h6">
                          Insurance requirements
                        </Typography>
                      </div>
                      <div css={tw`h-px bg-shades-100 mx-4 mb-4`} />
                      <div css={tw`px-4 pb-5`}>
                        <div
                          css={styles.requirementsContent}
                          dangerouslySetInnerHTML={{ __html: insuranceRequirements }}
                        />
                      </div>
                      <div css={tw`h-px bg-shades-100 mx-4`} />
                    </Fragment>
                  )}

                  {hasCoverageModalContent && (
                    <Fragment>
                      <div css={tw`flex flex-row items-center px-4 py-5`}>
                        <FraudProtectMajor width={16} css={tw`mr-2 fill-shades-900`} />
                        <Typography css={tw`text-base font-semibold text-shades-900`} variant="h6">
                          GigSafe coverage details
                        </Typography>
                      </div>
                      <div css={tw`h-px bg-shades-100 mx-4 mb-4`} />
                      <div css={tw`px-4 pb-4`}>
                        {costDisplay && (
                          <div css={tw`mb-3`}>
                            <Typography
                              css={tw`text-shades-900 text-base font-semibold mb-1`}
                              variant="h6"
                            >
                              Cost
                            </Typography>
                            <Typography css={tw`text-shades-400 text-base font-normal`}>
                              {costDisplay}
                            </Typography>
                          </div>
                        )}
                        {coverageDisplay && (
                          <div css={tw`mb-4`}>
                            <Typography
                              css={tw`text-shades-900 text-base font-semibold mb-1`}
                              variant="h6"
                            >
                              Coverage
                            </Typography>
                            <Typography css={tw`text-shades-400 text-base font-normal`}>
                              {coverageDisplay}
                            </Typography>
                          </div>
                        )}
                        <button
                          type="button"
                          css={tw`flex flex-row items-center bg-transparent border-none p-0 cursor-pointer text-primary-500 mb-2`}
                          onClick={handleOpenCoverageModal}
                          data-testid="contractors-onboarding-insurance-coverage-show-more-btn"
                        >
                          <Typography css={tw`text-primary-500 font-semibold`} variant="body2">
                            Show more
                          </Typography>
                          <span css={tw`ml-1 text-primary-500 font-semibold`}>›</span>
                        </button>
                      </div>
                    </Fragment>
                  )}
                </div>
              )}

              {optOutProduct.allowOptOut || stepOptional ? (
                <div css={tw`mb-4`}>
                  <Typography css={tw`uppercase text-shades-400 mb-2`} variant="overline">
                    Choose an option
                  </Typography>

                  <div
                    css={tw`rounded-2xl bg-shades-0 overflow-hidden p-4 flex flex-col gap-3 mt-2`}
                  >
                    <div
                      css={[
                        tw`cursor-pointer py-[14px] px-3`,
                        isGigSafeSelected
                          ? tw`rounded-lg border border-solid border-primary-500 bg-primary-25`
                          : tw`rounded-lg border border-solid border-[#DAD9E0] bg-shades-0`,
                        showTypeRequiredError && tw`border-critical-500`,
                      ]}
                      onClick={() => handleSelectGigSafe(setFieldValue)}
                      data-testid="contractors-onboarding-insurance-coverage-gigsafe-option"
                    >
                      <div css={tw`flex flex-row items-center`}>
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            flexShrink: 0,
                            border: isGigSafeSelected ? '5px solid #7F00FF' : '2px solid #DAD9E0',
                            backgroundColor: '#ffffff',
                            boxSizing: 'border-box',
                          }}
                        />
                        <div css={tw`ml-3 flex-1`}>
                          <div css={tw`flex flex-row items-center mb-1`}>
                            <Typography
                              css={tw`text-primary-500 font-semibold text-sm mr-1`}
                              variant="body2"
                            >
                              Easy &amp; affordable option
                            </Typography>
                            <FraudProtectMajor width={14} css={tw`fill-primary-500`} />
                          </div>
                          <Typography css={tw`font-semibold text-shades-900`} variant="h6">
                            Get GigSafe insurance
                          </Typography>
                        </div>
                      </div>
                      <ul css={tw`m-0 pl-0 list-none mt-4`}>
                        <li css={tw`flex flex-row items-start ml-2`}>
                          <span css={tw`text-shades-500 mr-2 mt-0.5`}>•</span>
                          <Typography css={tw`text-shades-500`} variant="body2">
                            Included in your weekly membership
                          </Typography>
                        </li>
                        <li css={tw`flex flex-row items-start ml-2`}>
                          <span css={tw`text-shades-500 mr-2 mt-0.5`}>•</span>
                          <Typography css={tw`text-shades-500`} variant="body2">
                            No extra action needed other than signing the Enrolment Form.
                          </Typography>
                        </li>
                      </ul>
                    </div>

                    <div
                      css={[
                        tw`cursor-pointer py-[14px] px-3`,
                        isOwnInsuranceSelected
                          ? tw`rounded-lg border border-solid border-primary-500 bg-primary-25`
                          : tw`rounded-lg border border-solid border-[#DAD9E0] bg-shades-0`,
                        showTypeRequiredError && tw`border-critical-500`,
                      ]}
                      onClick={() => handleSelectOwnInsurance(setFieldValue)}
                      data-testid="contractors-onboarding-insurance-coverage-own-option"
                    >
                      <div css={tw`flex flex-row items-center`}>
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            flexShrink: 0,
                            border: isOwnInsuranceSelected
                              ? '5px solid #7F00FF'
                              : '2px solid #DAD9E0',
                            backgroundColor: '#ffffff',
                            boxSizing: 'border-box',
                          }}
                        />
                        <Typography css={tw`ml-3 font-semibold text-shades-900`} variant="h6">
                          Use my own insurance
                        </Typography>
                      </div>
                    </div>
                  </div>
                  {showTypeRequiredError && (
                    <div css={tw`flex items-center gap-1 mt-2`}>
                      <RiskMinor width={20} css={tw`fill-critical-500`} />
                      <Typography variant="body2" css={tw`text-critical-500`} component="span">
                        Selecting an answer is required.
                      </Typography>
                    </div>
                  )}
                </div>
              ) : null}

              {isOwnInsuranceSelected && (
                <div css={tw`mb-4`}>
                  <div css={tw`rounded-2xl bg-shades-0 p-4 flex flex-col gap-4`}>
                    <Typography css={tw`uppercase text-shades-400`} variant="overline">
                      Provide your insurance
                    </Typography>

                    {ownInsuranceRequirements.length > 0 && (
                      <div>
                        <div
                          css={tw`flex flex-row items-center gap-4 pb-3`}
                          style={{ borderBottom: '1px solid #EDEDED' }}
                        >
                          <CircleInformationMajor
                            width={18}
                            css={tw`fill-[#FF6320] flex-shrink-0`}
                          />
                          <Typography
                            css={tw`font-semibold text-[#FF6320] text-base leading-5`}
                            variant="h6"
                          >
                            To get approved, make sure your insurance document includes:
                          </Typography>
                        </div>
                        <div css={tw`flex flex-col`}>
                          {ownInsuranceRequirements.map((requirement, index) => {
                            const isLast = index === ownInsuranceRequirements.length - 1

                            return (
                              <div
                                key={index}
                                css={tw`flex flex-row items-center py-3`}
                                style={{
                                  borderBottom: isLast ? '1px solid #EDEDED' : undefined,
                                  paddingBottom: isLast ? 16 : undefined,
                                  marginBottom: isLast ? 8 : undefined,
                                  paddingTop: isLast ? 4 : undefined,
                                }}
                              >
                                <span
                                  css={tw`text-[#655E7C] font-semibold mr-4 text-xs flex-shrink-0 rounded-xl bg-shades-100 w-5 h-5 flex items-center justify-center`}
                                >
                                  {index + 1}
                                </span>
                                <Typography
                                  css={tw`text-[#000] text-base font-normal leading-[22px]`}
                                  variant="body2"
                                >
                                  {requirement}
                                </Typography>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    <div>
                      {hasDocumentUploadTitleContent && (
                        <div css={tw`mb-4 p-4 bg-warning-200 rounded-xl`}>
                          <div
                            css={styles.ownUploadDescription}
                            dangerouslySetInnerHTML={{ __html: documentUploadTitleWhenOptedIn! }}
                          />
                        </div>
                      )}
                      <Typography
                        css={tw`text-shades-400 mb-2 text-sm font-semibold normal-case`}
                        variant="overline"
                      >
                        Upload proof of insurance
                      </Typography>
                      <FormikMultipleFileInput
                        name="files"
                        inputDataTestId="contractors-onboarding-insurance-coverage-files-input"
                        maxFiles={INSURANCE_COVERAGE_MAX_FILES}
                        maxFilesReachedDataTestId="contractors-onboarding-insurance-coverage-files-limit-info"
                      />
                      <Typography
                        css={tw`mt-3 text-shades-400 text-center text-sm`}
                        variant="body2"
                      >
                        Tap to upload or drag and drop
                      </Typography>
                      <Typography css={tw`text-shades-400 text-center text-xs`} variant="body2">
                        {`PNG, JPG or PDF (max ${Math.round(MAX_FILE_SIZE_BYTES / 1000000)}MB)`}
                      </Typography>
                    </div>
                  </div>

                  {ownInsuranceRequirements.length > 0 && (
                    <Fragment>
                      <Typography
                        css={tw`uppercase text-shades-400 mt-6 mb-3 block`}
                        variant="overline"
                      >
                        Confirm your insurance details
                      </Typography>
                      <div css={tw`bg-shades-0 p-4 rounded-xl`}>
                        <Typography
                          css={tw`text-shades-400 mb-4 text-sm font-semibold`}
                          variant="body2"
                        >
                          Check each item to confirm your insurance meets all the requirements
                        </Typography>
                        {ownInsuranceRequirements.map((requirement, index) => (
                          <div
                            key={index}
                            css={tw`mb-2 p-2 rounded-lg`}
                            style={
                              checkedRequirements[index]
                                ? { border: '1px solid #7F00FF', background: '#FDF9FF' }
                                : { border: '1px solid #DAD9E0', background: '#FFF' }
                            }
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!checkedRequirements[index]}
                                  onChange={() => {
                                    handleToggleRequirement(index)
                                  }}
                                  css={tw`p-1 mr-1`}
                                  sx={{
                                    color: '#DAD9E0',
                                    '&.Mui-checked': { color: '#7F00FF' },
                                  }}
                                  inputProps={
                                    {
                                      'aria-label': requirement,
                                      'data-testid': `contractors-onboarding-insurance-coverage-requirement-${index}-checkbox`,
                                    } as React.InputHTMLAttributes<HTMLInputElement>
                                  }
                                />
                              }
                              label={
                                <Typography
                                  css={tw`text-[#847E96] text-base font-normal`}
                                  variant="body2"
                                >
                                  {requirement}
                                </Typography>
                              }
                              css={tw`items-center m-0`}
                            />
                          </div>
                        ))}
                        {!allRequirementsChecked && (
                          <Typography css={tw`text-[#847E96] text-sm mt-3`} variant="body2">
                            All boxes must be checked to complete the step
                          </Typography>
                        )}
                      </div>
                    </Fragment>
                  )}
                </div>
              )}

              {stepRequireDocumentUploadWhenOptedIn && isGigSafeSelected && (
                <div css={tw`flex flex-col p-4 rounded-2xl bg-shades-0 mb-4`}>
                  {hasDocumentUploadTitleContent && (
                    <div css={[tw`mb-4 p-4 bg-warning-200 rounded-xl`]}>
                      <div
                        css={styles.ownUploadDescription}
                        dangerouslySetInnerHTML={{ __html: documentUploadTitleWhenOptedIn! }}
                      />
                    </div>
                  )}
                  <FormikMultipleFileInput
                    name="files"
                    label="Upload proof of insurance"
                    inputDataTestId="contractors-onboarding-insurance-coverage-proof-input"
                    maxFiles={INSURANCE_COVERAGE_MAX_FILES}
                    maxFilesReachedDataTestId="contractors-onboarding-insurance-coverage-proof-limit-info"
                  />
                </div>
              )}
            </div>
            {!signingIframeCrashed ? (
              <div
                id={DOCUSIGN_CONTAINER_ID}
                data-testid="contractors-onboarding-insurance-coverage-docusign-container"
                css={[
                  tw`fixed top-0 left-0 z-50 h-full w-full bg-shades-0 overflow-auto`,
                  docusignSigningData ? undefined : tw`opacity-0 hidden`,
                ]}
              />
            ) : null}
            <DocusignFieldsFormModal
              isOpen={isFieldsModalOpen}
              fields={modalFields}
              onClose={handleCloseFieldsModal}
              onSubmit={handleSubmitFieldsModal}
            />
            <GigSafeCoverageModal
              isOpen={isCoverageModalOpen}
              cost={costDisplay}
              coverage={coverageDisplay}
              coverageDetails={optOutProduct.coverageDetails}
              coverageDetailsPdfUrl={optOutProduct.coverageDetailsPdfUrl}
              onClose={handleCloseCoverageModal}
            />
          </SecureWrapper>
        )
      }}
    </Formik>
  )
}

export const InsuranceCoverageForm = memo(InsuranceCoverageFormComponent)

const styles = {
  requirementsContent: css`
    width: 100%;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    font-size: 16px;
    font-weight: 400;
    color: rgba(132, 126, 150, 1);
    p,
    span,
    div,
    * {
      color: rgba(132, 126, 150, 1);
      font-size: 16px;
      font-weight: 400;
      margin: 0;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
      max-width: 100%;
    }
    strong,
    b {
      color: #000000;
      font-weight: 600;
    }
    p:first-child {
      margin-top: 0;
    }
    p:last-child {
      margin-bottom: 0;
    }
  `,
  ownUploadDescription: css`
    width: 100%;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    p,
    span,
    div,
    * {
      color: var(--mui-warning-500) !important;
      margin: 0;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
      max-width: 100%;
    }
    p:first-child {
      margin-top: 0;
    }
    p:last-child {
      margin-bottom: 0;
    }
  `,
}
